import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import rmmbrLogo from '../assets/rmmbr.png';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const [cssVars, setCssVars] = useState(() => {
    if (typeof window !== 'undefined') {
      const root = getComputedStyle(document.documentElement);
      return {
        bgPrimary: root.getPropertyValue('--bg-primary').trim(),
        bgSecondary: root.getPropertyValue('--bg-secondary').trim(),
        textPrimary: root.getPropertyValue('--text-primary').trim(),
        textSecondary: root.getPropertyValue('--text-secondary').trim(),
        borderColor: root.getPropertyValue('--border-color').trim(),
        accent: root.getPropertyValue('--accent').trim(),
        accentHover: root.getPropertyValue('--accent-hover').trim()
      };
    }
    return { 
      bgPrimary: '#1a1816', 
      bgSecondary: '#2a2520', 
      textPrimary: '#ffffff',
      textSecondary: '#d1d5db',
      borderColor: '#3a352f',
      accent: '#D4A574',
      accentHover: '#C59D52'
    };
  });

  const { login, loginWithGoogle, loginWithGitHub, sendMagicLink, isCoach, isClient } = useAuth();
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const { theme } = useTheme();
  const navigate = useNavigate();

  const from = location.state?.from?.pathname || (isCoach ? '/coach' : '/client');

  // Check for OAuth errors in URL params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const errorParam = searchParams.get('error');

    if (errorParam === 'account_exists') {
      setError('An account with this email already exists. Please sign in with your password instead.');
    } else if (errorParam === 'oauth_failed') {
      setError('OAuth sign in failed. Please try again or use email/password.');
    } else if (errorParam === 'no_session') {
      setError('No session found. Please try signing in again.');
    }

    // Clear error param from URL
    if (errorParam) {
      searchParams.delete('error');
      navigate({ search: searchParams.toString() }, { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    const updateCssVars = () => {
      if (typeof window !== 'undefined') {
        const root = getComputedStyle(document.documentElement);
        setCssVars({
          bgPrimary: root.getPropertyValue('--bg-primary').trim(),
          bgSecondary: root.getPropertyValue('--bg-secondary').trim(),
          textPrimary: root.getPropertyValue('--text-primary').trim(),
          textSecondary: root.getPropertyValue('--text-secondary').trim(),
          borderColor: root.getPropertyValue('--border-color').trim(),
          accent: root.getPropertyValue('--accent').trim(),
          accentHover: root.getPropertyValue('--accent-hover').trim()
        });
      }
    };
    
    updateCssVars();
    const interval = setInterval(updateCssVars, 100);
    return () => clearInterval(interval);
  }, [theme]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      // Redirect based on user role
      // Note: user might not be immediately available, so check after a short delay
      setTimeout(() => {
        if (isCoach) {
          navigate('/coach', { replace: true });
        } else if (isClient) {
          navigate('/client', { replace: true });
        } else if (result.user?.role === 'coach') {
          navigate('/coach', { replace: true });
        } else {
          navigate('/client', { replace: true });
        }
      }, 100);
    } else {
      setError(result.error);
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ 
        backgroundColor: cssVars.bgPrimary || 'var(--bg-primary)',
        color: cssVars.textPrimary || 'var(--text-primary)'
      }}
    >
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src={rmmbrLogo} alt="RMMBR" className="h-8 w-auto" />
          </div>
          <p style={{ color: cssVars.textSecondary || 'var(--text-secondary)' }}>
            RMMBR Method - Client Sessions
          </p>
        </div>

        {/* Login Card */}
        <div 
          className="rounded-2xl shadow-xl p-8"
          style={{ 
            backgroundColor: cssVars.bgSecondary || 'var(--bg-secondary)',
            border: `1px solid ${cssVars.borderColor || 'var(--border-color)'}`
          }}
        >
          <h2 
            className="text-2xl font-bold mb-6"
            style={{ color: cssVars.textPrimary || 'var(--text-primary)' }}
          >
            Sign In
          </h2>

          {error && (
            <div 
              className="mb-4 p-4 rounded-lg text-sm"
              style={{
                backgroundColor: theme === 'dark' ? 'rgba(220, 38, 38, 0.1)' : 'rgba(254, 242, 242, 1)',
                border: `1px solid ${theme === 'dark' ? 'rgba(220, 38, 38, 0.3)' : 'rgba(254, 226, 226, 1)'}`,
                color: theme === 'dark' ? '#fca5a5' : '#dc2626'
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label 
                htmlFor="email" 
                className="block text-sm font-medium mb-2"
                style={{ color: cssVars.textSecondary || 'var(--text-secondary)' }}
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg transition focus:outline-none"
                style={{
                  backgroundColor: cssVars.bgPrimary || 'var(--bg-primary)',
                  border: `1px solid ${cssVars.borderColor || 'var(--border-color)'}`,
                  color: cssVars.textPrimary || 'var(--text-primary)'
                }}
                placeholder="you@example.com"
                onFocus={(e) => {
                  const accent = cssVars.accent || '#D4A574';
                  e.target.style.borderColor = accent;
                  // Convert hex to rgba for opacity
                  const r = parseInt(accent.slice(1, 3), 16);
                  const g = parseInt(accent.slice(3, 5), 16);
                  const b = parseInt(accent.slice(5, 7), 16);
                  e.target.style.boxShadow = `0 0 0 2px rgba(${r}, ${g}, ${b}, 0.2)`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = cssVars.borderColor || 'var(--border-color)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div>
              <label 
                htmlFor="password" 
                className="block text-sm font-medium mb-2"
                style={{ color: cssVars.textSecondary || 'var(--text-secondary)' }}
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg transition focus:outline-none"
                style={{
                  backgroundColor: cssVars.bgPrimary || 'var(--bg-primary)',
                  border: `1px solid ${cssVars.borderColor || 'var(--border-color)'}`,
                  color: cssVars.textPrimary || 'var(--text-primary)'
                }}
                placeholder="••••••••"
                onFocus={(e) => {
                  const accent = cssVars.accent || '#D4A574';
                  e.target.style.borderColor = accent;
                  // Convert hex to rgba for opacity
                  const r = parseInt(accent.slice(1, 3), 16);
                  const g = parseInt(accent.slice(3, 5), 16);
                  const b = parseInt(accent.slice(5, 7), 16);
                  e.target.style.boxShadow = `0 0 0 2px rgba(${r}, ${g}, ${b}, 0.2)`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = cssVars.borderColor || 'var(--border-color)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none"
              style={{
                backgroundColor: cssVars.accent || 'var(--accent)',
                color: '#1a1816'
              }}
              onFocus={(e) => {
                const accent = cssVars.accent || '#D4A574';
                // Convert hex to rgba for opacity
                const r = parseInt(accent.slice(1, 3), 16);
                const g = parseInt(accent.slice(3, 5), 16);
                const b = parseInt(accent.slice(5, 7), 16);
                e.target.style.boxShadow = `0 0 0 3px rgba(${r}, ${g}, ${b}, 0.3)`;
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = cssVars.accentHover || 'var(--accent-hover)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = cssVars.accent || 'var(--accent)';
              }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* OAuth Divider */}
          <div className="relative my-6">
            <div 
              className="absolute inset-0 flex items-center"
              style={{ borderColor: cssVars.borderColor || 'var(--border-color)' }}
            >
              <div 
                className="w-full border-t"
                style={{ borderColor: cssVars.borderColor || 'var(--border-color)' }}
              ></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span style={{ color: cssVars.textSecondary || 'var(--text-secondary)', backgroundColor: cssVars.bgSecondary || 'var(--bg-secondary)' }} className="px-2">
                Or continue with
              </span>
            </div>
          </div>

          {/* OAuth Buttons */}
          <div className="space-y-3">
            {/* GitHub Sign In Button */}
            <button
              type="button"
              onClick={async () => {
                setLoading(true);
                const result = await loginWithGitHub();
                if (!result.success) {
                  setError(result.error);
                  setLoading(false);
                }
              }}
              disabled={loading}
              className="w-full py-3 px-4 rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none flex items-center justify-center gap-3"
              style={{
                backgroundColor: cssVars.bgPrimary || 'var(--bg-primary)',
                border: `1px solid ${cssVars.borderColor || 'var(--border-color)'}`,
                color: cssVars.textPrimary || 'var(--text-primary)'
              }}
              onFocus={(e) => {
                const accent = cssVars.accent || '#D4A574';
                const r = parseInt(accent.slice(1, 3), 16);
                const g = parseInt(accent.slice(3, 5), 16);
                const b = parseInt(accent.slice(5, 7), 16);
                e.target.style.boxShadow = `0 0 0 2px rgba(${r}, ${g}, ${b}, 0.2)`;
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = cssVars.bgSecondary || 'var(--bg-secondary)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = cssVars.bgPrimary || 'var(--bg-primary)';
              }}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
              </svg>
              <span>Continue with GitHub</span>
            </button>

            {/* Google Sign In Button (optional - can be removed if not needed) */}
            <button
              type="button"
              onClick={async () => {
                setLoading(true);
                const result = await loginWithGoogle();
                if (!result.success) {
                  setError(result.error);
                  setLoading(false);
                }
              }}
              disabled={loading}
              className="w-full py-3 px-4 rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none flex items-center justify-center gap-3"
              style={{
                backgroundColor: cssVars.bgPrimary || 'var(--bg-primary)',
                border: `1px solid ${cssVars.borderColor || 'var(--border-color)'}`,
                color: cssVars.textPrimary || 'var(--text-primary)'
              }}
              onFocus={(e) => {
                const accent = cssVars.accent || '#D4A574';
                const r = parseInt(accent.slice(1, 3), 16);
                const g = parseInt(accent.slice(3, 5), 16);
                const b = parseInt(accent.slice(5, 7), 16);
                e.target.style.boxShadow = `0 0 0 2px rgba(${r}, ${g}, ${b}, 0.2)`;
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = cssVars.bgSecondary || 'var(--bg-secondary)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = cssVars.bgPrimary || 'var(--bg-primary)';
              }}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span>Continue with Google</span>
            </button>
          </div>

          {/* Magic Link Option */}
          {!showMagicLink ? (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setShowMagicLink(true)}
                className="text-sm font-medium hover:underline transition"
                style={{ 
                  color: cssVars.accent || 'var(--accent)'
                }}
              >
                Or sign in with a magic link (no password needed)
              </button>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {magicLinkSent ? (
                <div 
                  className="p-4 rounded-lg text-sm"
                  style={{
                    backgroundColor: theme === 'dark' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(240, 253, 244, 1)',
                    border: `1px solid ${theme === 'dark' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(187, 247, 208, 1)'}`,
                    color: theme === 'dark' ? '#86efac' : '#16a34a'
                  }}
                >
                  Check your email! We sent you a magic link to sign in.
                </div>
              ) : (
                <>
                  <input
                    type="email"
                    value={magicLinkEmail}
                    onChange={(e) => setMagicLinkEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 rounded-lg transition focus:outline-none"
                    style={{
                      backgroundColor: cssVars.bgPrimary || 'var(--bg-primary)',
                      border: `1px solid ${cssVars.borderColor || 'var(--border-color)'}`,
                      color: cssVars.textPrimary || 'var(--text-primary)'
                    }}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        if (!magicLinkEmail) {
                          setError('Please enter your email');
                          return;
                        }
                        setLoading(true);
                        const result = await sendMagicLink(magicLinkEmail);
                        if (result.success) {
                          setMagicLinkSent(true);
                        } else {
                          setError(result.error);
                        }
                        setLoading(false);
                      }}
                      disabled={loading}
                      className="flex-1 py-2 px-4 rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: cssVars.accent || 'var(--accent)',
                        color: '#1a1816'
                      }}
                    >
                      Send Magic Link
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowMagicLink(false);
                        setMagicLinkEmail('');
                        setMagicLinkSent(false);
                      }}
                      className="px-4 py-2 rounded-lg font-medium transition"
                      style={{
                        backgroundColor: cssVars.bgPrimary || 'var(--bg-primary)',
                        border: `1px solid ${cssVars.borderColor || 'var(--border-color)'}`,
                        color: cssVars.textPrimary || 'var(--text-primary)'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="mt-6 text-center">
            <p 
              className="text-sm"
              style={{ color: cssVars.textSecondary || 'var(--text-secondary)' }}
            >
              Have an invitation code?{' '}
              <Link
                to="/client/signup"
                className="font-medium hover:underline transition"
                style={{ 
                  color: cssVars.accent || 'var(--accent)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = cssVars.accentHover || 'var(--accent-hover)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = cssVars.accent || 'var(--accent)';
                }}
              >
                Create your account
              </Link>
            </p>
          </div>

          <div 
            className="mt-8 pt-6 border-t"
            style={{ borderColor: cssVars.borderColor || 'var(--border-color)' }}
          >
            <p 
              className="text-xs text-center"
              style={{ color: cssVars.textSecondary || 'var(--text-secondary)' }}
            >
              For coach registration, please contact your administrator
            </p>
          </div>
        </div>

        {/* Footer */}
        <div 
          className="mt-8 text-center text-sm"
          style={{ color: cssVars.textSecondary || 'var(--text-secondary)' }}
        >
          <p>Need help? Contact support</p>
        </div>
      </div>
    </div>
  );
}

export default Login;
